// lib/leasing/source-view-guards.ts
//
// The two DECISIONS the Source center's effects act on, extracted so they can be stated and
// pinned as behaviour instead of living untestably inside useEffect bodies. Both exist because
// each shipped wrong once, in ways a unit test on the decision would have caught:
//
// - The completion recall fired on ANY visit to a finished campaign carrying
//   recallCommanderOnComplete, so merely opening yesterday's run started a full unprompted
//   agent turn - tokens spent, the user's focus moved, every visit, forever.
// - The selection validity check treated an ABSENT (not-yet-hydrated) campaign row as invalid,
//   so booting into a campaign-bound conversation wiped the selection and left the campaign's
//   chat talking over the dashboard.

/** Campaign lifecycle states in which a run has LANDED (there is a ranking to speak about). */
const TERMINAL_CAMPAIGN_STATUSES = new Set(['COMPLETE', 'ABORTED', 'FAILED']);

import { resolveCampaignPropertyIds } from '#lib/leasing/sourcingCampaignLifecycle';
import type { KernelState } from '#types/types';

export const sourceRentRollOwnsCanvas = (state: Pick<KernelState, 'activeChatSessionId' | 'inventory' | 'sourceOpeningRentRollId'>): boolean => {
  const subject = state.activeChatSessionId ? state.inventory.ChatSession.registry[state.activeChatSessionId]?.activeSubject : null;
  return Boolean(state.sourceOpeningRentRollId || (subject?.entityType === 'Property' && subject.focusActionType === 'RENT_ROLL'));
};

/**
 * How wide the OPEN campaign reached. There is only ONE sourcing operation; this is not a mode the
 * user picks, it is a property of the run that is on screen, read off its own row.
 */
export type OpenCampaignScope = 'portfolio' | 'asset';

/** The slice of kernel state this resolution needs, so both components and headless code can pass it. */
export interface OpenCampaignScopeState {
  sourceSelectedCampaignId?: string | null;
  inventory?: {
    Campaign?: { registry?: Record<string, { targetKind?: unknown } | undefined> };
    PortfolioFitCampaign?: { registry?: Record<string, unknown> };
  };
}

/**
 * What the campaign currently open in Source covers, derived from its own `targetKind`.
 *
 * This replaces the old `sourceCampaignScope` MODE. A user never picks portfolio / asset any more:
 * they pick assets (or none) and the launch routes itself, so the only thing left that can answer
 * "which table, which lock, which panel" is the run on screen.
 *
 * The unified Campaign registry is the source; the two legacy registries are a fallback for rows
 * that were hydrated by an older list route and never entered it. With nothing open the answer is
 * `asset`, the shape of every conversation-bound run and of a fresh draft.
 */
export function resolveOpenCampaignScope(
  state: OpenCampaignScopeState | null | undefined,
): OpenCampaignScope {
  const campaignId = typeof state?.sourceSelectedCampaignId === 'string' ? state.sourceSelectedCampaignId.trim() : '';
  if (!campaignId) return 'asset';
  const inventory = state?.inventory;
  const targetKind = inventory?.Campaign?.registry?.[campaignId]?.targetKind;
  if (targetKind === 'PORTFOLIO') return 'portfolio';
  if (targetKind === 'ASSET') return 'asset';
  if (inventory?.PortfolioFitCampaign?.registry?.[campaignId]) return 'portfolio';
  return 'asset';
}

export interface RecallCommanderDecisionInput {
  /** The open campaign's status, any casing. */
  status: string | null | undefined;
  /** config.recallCommanderOnComplete carried on the launch payload. */
  recallWanted: unknown;
  /** Did THIS browser session see the campaign running? The licence for spontaneous speech. */
  witnessed: boolean;
  /** Has this mount already narrated this campaign? (once per campaign, no stutter) */
  alreadyNarrated: boolean;
  /** Is there a conversation to speak in? */
  hasSession: boolean;
}

/**
 * Whether the agent may pick the conversation back up on its own after a campaign reaches a
 * terminal status. The load-bearing rule: only a landing this session actually WATCHED. An old
 * finished campaign being opened is indistinguishable from that landing by status alone, and
 * must stay silent.
 */
export function shouldRecallDealerOnLanding(input: RecallCommanderDecisionInput): boolean {
  if (!TERMINAL_CAMPAIGN_STATUSES.has(String(input.status ?? '').toUpperCase())) return false;
  if (input.recallWanted !== true) return false;
  if (!input.witnessed) return false;
  if (input.alreadyNarrated) return false;
  return input.hasSession;
}

export interface SelectionValidityInput {
  /** The selected campaign's row, or undefined when the registry has not (yet) got it. */
  selectedCampaign: { propertyId?: string; portfolioId?: string; config?: unknown } | undefined;
  scope: 'portfolio' | 'asset' | 'unit' | string | null | undefined;
  /** The Source view's focused entity. */
  entityType: 'Portfolio' | 'Property' | 'Unit' | string;
  entityId: string;
  resolvedPortfolioId: string | null | undefined;
}

/**
 * Whether the open-campaign selection may stay. An ABSENT row is "not hydrated yet", never
 * "wrong": at boot a bound conversation restores the selection before any registry has loaded.
 * Only a PRESENT row whose ownership contradicts the focused entity proves the selection wrong
 * (a campaign opened for P1 must not stay selected after navigating to P2).
 */
export function isCampaignSelectionStillValid(input: SelectionValidityInput): boolean {
  const { selectedCampaign, scope, entityType, entityId, resolvedPortfolioId } = input;
  if (!selectedCampaign) return true;
  if (scope === 'portfolio') {
    // Absent ownership tolerates, exactly like the Property branch below: a just-launched
    // portfolio campaign can exist before its portfolioId is stamped, and wiping it throws the
    // user back to the dashboard mid-launch. Only a PRESENT, contradicting id clears.
    return (
      !resolvedPortfolioId ||
      !selectedCampaign.portfolioId ||
      selectedCampaign.portfolioId === resolvedPortfolioId
    );
  }
  if (entityType === 'Property') {
    if (!selectedCampaign.propertyId) return true;
    // EVERY asset the run covers, not just the one its column names. A run over three assets is one
    // campaign; focusing the second of them is navigating INSIDE the open run, not away from it,
    // and clearing the selection there dropped the user back to the dashboard from the shortlist
    // they were reading.
    return resolveCampaignPropertyIds(selectedCampaign.config, selectedCampaign.propertyId).includes(entityId);
  }
  return true;
}

/**
 * Lead statuses the shortlist shows WHILE a pass is still building it.
 *
 * Every brand the run picked shows the moment it is launched (PENDING, then RUNNING while it is
 * researched and scored), with no score until it has one. Holding them back until scored left the
 * table empty for the whole run, three minutes on two brands, which read as a launch that did
 * nothing (owner, 2026-09-27: "the launch should be instantaneous"). Verdicts show as always.
 *
 * Still held back until the run's outcome is complete: FAILED / ABORTED / REVERTED.
 */
export const REVEALED_WHILE_BUILDING = new Set(['PENDING', 'RUNNING', 'SUCCESS', 'INTERESTING', 'DISMISSED', 'INVALIDATED']);

/** Is this lead fit to show while its campaign is still running? */
export function isLeadRevealedWhileBuilding(status: string | null | undefined): boolean {
  return REVEALED_WHILE_BUILDING.has(String(status ?? '').trim().toUpperCase());
}

/** A lead the pass has claimed but not finished. Seeded PENDING, flipped to RUNNING while scored. */
const LEAD_IN_FLIGHT_STATUSES = new Set(['PENDING', 'RUNNING']);

/**
 * IS A PASS ACTUALLY WORKING RIGHT NOW - decided by the leads, not by the campaign's own status.
 *
 * Measured on campaign cmsep2w0500ajxq5c88e48n61 mid-extension: the database held WAITING and four
 * PENDING leads while the client's campaign row still read COMPLETE, because nothing pushes a
 * status change back to an open page. Every downstream answer keyed off that stale word - the
 * reveal gate stayed off, and the count of rows still coming was zero BY CONSTRUCTION, so adding
 * brands to a shortlist showed no sign of work at all.
 *
 * A lead that exists and is not finished is a fact about this run that cannot lag: the row is
 * written before the work starts. Callers keep their status check as the fast path and OR this in.
 */
export function isLeadInFlight(status: unknown): boolean {
  return LEAD_IN_FLIGHT_STATUSES.has(String(status ?? '').trim().toUpperCase());
}

export function countLeadsInFlight(
  leads: ReadonlyArray<{ status?: string | null }>,
): number {
  return leads.filter((lead) => isLeadInFlight(lead?.status)).length;
}

/** The kernel-state slice deciding whether the Source dashboard owns the canvas alone. */
export interface SourceDashboardState {
  sourceHeaderIsDashboard?: boolean | null;
  sourceCreatingCampaign?: boolean | null;
  sourcePreparingCampaign?: boolean | null;
  sourceSelectedCampaignId?: string | null;
}

/**
 * TRUE when the campaign dashboard is the page on screen and nothing is drafted or open. The
 * dashboard carries NO right panel (2026-08-23): its one action, creating a campaign, is the
 * glowing button at the bottom of the canvas. Every right-bar surface stands down on THIS answer
 * rather than keeping its own copy of the rule - the header/right-bar/canvas disagreement of
 * sourceDashboardAgreement.test.ts is exactly what per-surface copies produced.
 */
export const sourceDashboardOwnsCanvas = (state: SourceDashboardState): boolean =>
  Boolean(state.sourceHeaderIsDashboard) &&
  !state.sourceCreatingCampaign &&
  !state.sourcePreparingCampaign &&
  !state.sourceSelectedCampaignId;
